import { useEffect, useRef, useState } from 'react'

const toDmy = (iso: string) => (/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso.slice(8, 10)}-${iso.slice(5, 7)}-${iso.slice(0, 4)}` : '')

/** dd-mm-yyyy → yyyy-mm-dd, or '' when it is not a real calendar date. */
const toIso = (dmy: string) => {
  const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(dmy)
  if (!m) return ''
  const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]))
  if (d.getFullYear() !== Number(m[3]) || d.getMonth() !== Number(m[2]) - 1 || d.getDate() !== Number(m[1])) return ''
  return `${m[3]}-${m[2]}-${m[1]}`
}

/**
 * Date box that always shows and accepts dd-mm-yyyy (the browser's own date input follows the PC's regional setting).
 * `value` and `onChange` use ISO yyyy-mm-dd. A calendar button opens the native picker.
 */
export function DateInputDMY({ value, max, onChange, className, testId }: { value: string; max?: string; onChange: (iso: string) => void; className?: string; testId?: string }) {
  const [text, setText] = useState(toDmy(value))
  const pickerRef = useRef<HTMLInputElement>(null)
  useEffect(() => { setText(toDmy(value)) }, [value])

  const type = (raw: string) => {
    // Digits only, dashes inserted automatically: 07102026 → 07-10-2026
    const d = raw.replace(/\D/g, '').slice(0, 8)
    const t = d.length > 4 ? `${d.slice(0, 2)}-${d.slice(2, 4)}-${d.slice(4)}` : d.length > 2 ? `${d.slice(0, 2)}-${d.slice(2)}` : d
    setText(t)
    const iso = toIso(t)
    if (iso && (!max || iso <= max)) onChange(iso)
  }
  const settle = () => setText(toDmy(value)) // an incomplete or invalid entry snaps back to the last good date

  return (
    <div className="inline-flex w-full items-center gap-1">
      <input type="text" inputMode="numeric" autoComplete="off" placeholder="dd-mm-yyyy" maxLength={10} value={text} onChange={(e) => type(e.target.value)} onBlur={settle} className={className} data-testid={testId} style={{ marginBottom: 0 }} />
      <button type="button" className="btn-ghost" style={{ padding: '6px 9px' }} title="Pick from calendar" aria-label="Pick from calendar" onClick={() => (pickerRef.current as any)?.showPicker?.() ?? pickerRef.current?.click()}>📅</button>
      <input ref={pickerRef} type="date" value={value} max={max} onChange={(e) => e.target.value && onChange(e.target.value)} tabIndex={-1} aria-hidden="true" style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }} />
    </div>
  )
}
